// This file is only a template for BaseAppComponents.  Copy and paste this to your application .tsx file and start off from there
// Rename all things with App_Name.
// Naming convention for BaseAppComponents is "[App Name]App.tsx" i.e. UsersApp.tsx, EntitlementApp.tsx

import React from 'react';
import UserLocaleStore from './../stores/UserLocaleStore';
import UserLocaleActions from './../actions/UserLocaleActions';
import UserLocaleConstants from '../constants/UserLocaleConstants';
import SystemActions from './../../../actions/SystemActions';
import SystemStore from './../../../stores/SystemStore';
import BaseAppComponent from './../../../components/class/BaseAppComponent';
import ReactLoader from './../../../components/core/ReactLoader';
import If from './../../../components/controls/If';
import DateTime from '../../../common/DateTime/DateTime';
declare var InovxLoader: any;

interface UserLocaleProps {
    onClose: () => void;
}

interface UserLocaleState {
    localeData: { Cultures: ICultureData[], TimeZones: ITimeZone[] };
    editUserLocale: ILocalePreferences;   
    isActionInProgress: boolean;
    dirty: boolean;
    defaultCulture: ICultureData;
    defaultTimeZone: ITimeZone;
}

function getActionInProgress() {
    return SystemStore.isActionInProgress(UserLocaleConstants.LOCALE_PREFERENCES_UPDATE) || SystemStore.isActionInProgress(UserLocaleConstants.LOCALE_GET);
}

function getLocaleState(): UserLocaleState {
    var locale = UserLocaleStore.getEditLocale();
    var userLocale: ILocalePreferences = null;
    var defaultCulture: ICultureData = null;
    var defaultTimeZone: ITimeZone = null;

    if (locale && locale.Cultures.length > 0) {
        defaultCulture = locale.Cultures[0];
    }

    if (locale && locale.TimeZones.length > 0) {
        defaultTimeZone = locale.TimeZones[0];
    }   

    if (defaultCulture != null && defaultTimeZone != null) {
        if (locale.UserLocale != null) {
            userLocale = {
                LanguageCode: locale.UserLocale.LanguageCode,
                DateFormat: locale.UserLocale.DateFormat,
                TimeFormat: locale.UserLocale.TimeFormat,
                TimeZoneID: locale.UserLocale.TimeZoneID
            };
        }
        else
            userLocale = { LanguageCode: null, DateFormat: null, TimeFormat: null, TimeZoneID: null }
    }

    return {
        localeData: locale,
        editUserLocale: userLocale,        
        defaultCulture: defaultCulture,
        defaultTimeZone: defaultTimeZone,
        dirty: false,
        isActionInProgress: false//getActionInProgress()               
    };
}

class UserLocaleApp extends BaseAppComponent<UserLocaleProps, UserLocaleState> {

    private _selectedCulture: ICultureData;
    private _selectedDateFormat: string;
    private _selectedTimeFormat: string;
    private _selectedTimeZone: ITimeZone;

    private _getAllowedCultures = (cultures: ICultureData[]) => {
        return cultures.filter((culture) => {
            var languageCode = culture && culture.LanguageCode ? culture.LanguageCode.toLowerCase() : null;
            return languageCode === 'en-us' || languageCode === 'ja-jp';
        });
    }

    constructor(props: UserLocaleProps, context: any) {
        super('UserLocale', props, context);

        this._initialize = this._initialize.bind(this);       
        this.state = getLocaleState();
    }

    protected _initialize() {
        UserLocaleActions.getLocale(
            DateTime.getLocalTimezone(),
            () => this._appLoaded = true,
            (error: any) => this._handlePermissionError(error),
            true
        );
    }

    private _validateAndUpdateState(newState: UserLocaleState) {
        const locale = newState.localeData;
        const userLocaleState = newState.editUserLocale;
        const allowedCultures = this._getAllowedCultures(locale.Cultures);
        const defaultCulture = allowedCultures.filter((culture) => culture.LanguageCode === newState.defaultCulture.LanguageCode)[0] || allowedCultures[0] || newState.defaultCulture;

        if (userLocaleState.LanguageCode) {
            let selectedCulture: ICultureData = null;
            // array.find is not supported in IE11 :-(
            for (var i = 0; i < allowedCultures.length; i++) {
                if (allowedCultures[i].LanguageCode === userLocaleState.LanguageCode) {
                    selectedCulture = allowedCultures[i];
                    break;
                }
            }
            if (!selectedCulture) {
                userLocaleState.LanguageCode = defaultCulture.LanguageCode;
                selectedCulture = defaultCulture;
            }
            this._selectedCulture = selectedCulture;
        }
        else {
            this._selectedCulture = defaultCulture;
        }

        if (userLocaleState.DateFormat) {
            let dateFormatIsValid = false;
            for (var i = 0; i < this._selectedCulture.DateFormats.length; i++) {
                if (this._selectedCulture.DateFormats[i].Format === userLocaleState.DateFormat) {
                    dateFormatIsValid = true;
                    break;
                }
            }
            if (!dateFormatIsValid) {
                userLocaleState.DateFormat = this._selectedCulture.DateFormats[0].Format;
            }

            this._selectedDateFormat = userLocaleState.DateFormat;
        }
        else {
            this._selectedDateFormat = this._selectedCulture.DateFormats[0].Format;
        }

        if (userLocaleState.TimeFormat) {
            let timeFormatIsValid = false;
            for (var i = 0; i < this._selectedCulture.TimeFormats.length; i++) {
                if (this._selectedCulture.TimeFormats[i].Format === userLocaleState.TimeFormat) {
                    timeFormatIsValid = true;
                    break;
                }
            }
            if (!timeFormatIsValid) {
                userLocaleState.TimeFormat = this._selectedCulture.TimeFormats[0].Format;
            }

            this._selectedTimeFormat = userLocaleState.TimeFormat;
        }
        else {
            this._selectedTimeFormat = this._selectedCulture.TimeFormats[0].Format;
        }

        if (userLocaleState.TimeZoneID) {
            let selectedTimeZone: ITimeZone = null;
            // array.find is not supported in IE11 :-(
            for (var i = 0; i < locale.TimeZones.length; i++) {
                if (locale.TimeZones[i].ID === userLocaleState.TimeZoneID) {
                    selectedTimeZone = locale.TimeZones[i];
                    break;
                }
            }
            if (!selectedTimeZone) {
                userLocaleState.TimeZoneID = newState.defaultTimeZone.ID;
                selectedTimeZone = newState.defaultTimeZone;
            }
            this._selectedTimeZone = selectedTimeZone;
        }
        else {
            this._selectedTimeZone = newState.defaultTimeZone;
        }

        this.setState(newState);
    }

    private _onChange = () => {
        this._validateAndUpdateState(getLocaleState());
    }

    private _onSystemChange = () => {
        this.setState({
            isActionInProgress: getActionInProgress()
        } as any);
    }

    private _onCancel = (e: any) => {
        if (this.state.dirty) {
            SystemActions.PublicActions.displaySaveChangesDialog({
                message: InovxLoader.t("common:msgBox.saveChanges"),
                confirmCallback: () => {
                    this._save();
                },
                cancelCallback: () => {
                    this.props.onClose();
                }
            });
        }
        else {
            UserLocaleActions.cancelChanges();
            this.props.onClose();
        }
    }

    private _save = () => {        
        UserLocaleActions.updateLocale(this.state.editUserLocale, () => {
            this.props.onClose();
            window.location.reload();
        }, null);
    }

    private _onLanguageChanged = (e: any) => {
        var value = e.target.value;
        this.state.editUserLocale.LanguageCode = value;

        this._validateAndUpdateState(this.state);
        this.setState({ dirty: true });        
    }

    private _onDatePatternChanged = (e: any) => {
        var value = e.target.value;
        this.state.editUserLocale.DateFormat = value;

        this._validateAndUpdateState(this.state);
        this.setState({ dirty: true });
    }

    private _onTimePatternChanged = (e: any) => {
        var value = e.target.value;        
        this.state.editUserLocale.TimeFormat = value;

        this._validateAndUpdateState(this.state);
        this.setState({ dirty: true });
    }

    private _onTimeZoneChanged = (e: any) => {
        var value = e.target.value;
        this.state.editUserLocale.TimeZoneID = value;

        this._validateAndUpdateState(this.state);
        this.setState({ dirty: true });
    }   

    UNSAFE_componentWillMount() {

        this.componentWillMountBase();
    }

    componentDidMount() {
        UserLocaleStore.addChangeListener(this._onChange);
        SystemStore.addChangeListener(this._onSystemChange);
    }

    componentWillUnmount() {
        UserLocaleStore.removeChangeListener(this._onChange);
        SystemStore.removeChangeListener(this._onSystemChange);
    }

    public render() {      
        var selectedDateFormat = "";
        var selectedTimeFormat = "";       
        var languageOptions: any[] = [];
        var datePatternOptions: any[] = [];
        var timePatternOptions: any[] = [];
        var timeZoneOptions: any[] = [];       
        if (this.isAppLoaded()) {
            var locale = this.state.localeData;
            var userLocale = this.state.editUserLocale;
            var languages: ICultureData[] = this._getAllowedCultures(locale.Cultures);
            var timeZones: ITimeZone[] = locale.TimeZones;           
            
            if (userLocale != null) {
                var selectedTimeZone = this._selectedTimeZone;
                var selectedCulture = this._selectedCulture;

                for (var i in languages) {
                    languageOptions.push(<option value={languages[i].LanguageCode} key={i}>{languages[i].DisplayName}</option>);
                }

                for (var i in selectedCulture.DateFormats) {
                    datePatternOptions.push(<option value={selectedCulture.DateFormats[i].Format} key={i}>{selectedCulture.DateFormats[i].DisplayFormat}</option >);
                }

                for (var i in selectedCulture.TimeFormats) {
                    timePatternOptions.push(<option value={selectedCulture.TimeFormats[i].Format} key={i}>{selectedCulture.TimeFormats[i].DisplayFormat}</option >);
                }                

                for (var i in timeZones) {
                    timeZoneOptions.push(<option value={timeZones[i].ID} key={i}>{timeZones[i].DisplayName}</option>);
                }

                selectedDateFormat = this._selectedDateFormat;
                selectedTimeFormat = this._selectedTimeFormat;
            }
        }        

        return (
            <ReactLoader appReady={this.isAppLoaded()} errorStatus={this._errorStatus}>
                <div className="panel panel-primary user-locale-app-content">
                    <div className="panel-heading">{InovxLoader.t('UserLocale:header.locale')}
                        <a href="#" onClick={this._onCancel} >
                            <span className="pull-right glyphicon glyphicon glyphicon-remove"></span>
                        </a>
                    </div>
                    <div className="panel-body user-locale-body">
                        <form>
                            <div className="row user-locale-row">
                                <label className="col-sm-5 pull-left">{InovxLoader.t('UserLocale:form.language')}</label>
                                <select className="col-xm-7 user-locale-selector" onChange={this._onLanguageChanged} value={this._selectedCulture ? this._selectedCulture.LanguageCode : ""}>
                                    {languageOptions}
                                </select>
                            </div>
                            <div className="row user-locale-row">
                                <label className="col-sm-5 pull-left">{InovxLoader.t('UserLocale:form.datePattern')}</label>
                                <select className="col-xm-7 user-locale-selector" onChange={this._onDatePatternChanged} value={selectedDateFormat}>
                                {datePatternOptions}
                                </select>
                            </div>

                            <div className="row user-locale-row">
                                <label className="col-sm-5 pull-left">{InovxLoader.t('UserLocale:form.timePattern')}</label>
                                <select className="col-xm-7 user-locale-selector" onChange={this._onTimePatternChanged} value={selectedTimeFormat}>
                                    {timePatternOptions}
                                </select>
                            </div>

                            <div className="row user-locale-row">
                                <label className="col-sm-5 pull-left">{InovxLoader.t('UserLocale:form.timeZone')}</label>
                                <select className="col-xm-7 user-locale-selector" onChange={this._onTimeZoneChanged} value={this._selectedTimeZone ? this._selectedTimeZone.ID : ""}>
                                    {timeZoneOptions}
                                </select>
                            </div>
                                
                        </form>                           
                    </div>
                    <div className="panel-footer clearfix">
                        <div className="row">                                
                            <div className="pull-right">
                                <button type="button" id="user-locale.btn.save" className="btn btn-primary btn-space" onClick={this._save}>{InovxLoader.t("common:form.save")}</button>
                                <button type="button" id="user-locale.btn.cancel" className="btn btn-primary btn-right" onClick={this._onCancel}>{InovxLoader.t("common:form.cancel")}</button>
                            </div>
                        </div>
                    </div>
                </div>
            </ReactLoader>                 
        );
    }
}
export default UserLocaleApp;
